import { useEffect } from 'react';
import { lt, useUrlSyncedTableState } from '@mews/framework';
import { Table, TableProvider, TableHeader, EmptyStateProps } from '@optimus-web/core';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { DepositLedgerTableRecord } from '@mews-commander/data-access';
import { useColumns } from '../hooks/useTableMapping';
import { useDepositLedgerTableQuery } from '../hooks/useDepositLedgerTableQuery';
import { useDepositLedgerFilterContext, useDepositLedgerSelectedDate } from '../context/DepositLedgerFilterContext';
import { transformFilterValues } from '../../common/utils/transformFilterValues';
import { DepositLedgerTableFilters } from './DepositLedgerTableFilters';

export const DepositLedgerTable = () => {
    const columns = useColumns();
    const { filterValues } = useDepositLedgerFilterContext();
    const transformedFilterValues = transformFilterValues(filterValues);
    const { selectedDate } = useDepositLedgerSelectedDate();
    const [tableState, setTableState] = useUrlSyncedTableState<DepositLedgerTableRecord>({
        columns,
        extraQuery: { date: selectedDate, filterValues: transformedFilterValues },
    });

    const { data: tableData, isLoading, error } = useDepositLedgerTableQuery(tableState);
    const emptyStateMessage: EmptyStateProps = error
        ? { primaryText: error.message, pictogram: 'system_error' }
        : { primaryText: lt('NoDataAvailable') };

    useEffect(() => {
        setTableState({
            ...tableState,
            pagination: { ...tableState.pagination, pageIndex: 0 },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValues, selectedDate]);

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={lt('DepositLedger')}
                subTitle={
                    <span data-test-id="deposit-ledger-table-subtitle">
                        <LocalizedDateFromUtc utcString={selectedDate} />
                    </span>
                }
                globalSearch={{ placeholder: lt('LedgersSearchPlaceholder'), minWidth: '100%', maxCharacterCount: 120 }}
                rightSide={<DepositLedgerTableFilters />}
            />
            <Table
                uniqueRowId="id"
                amountOfRows={tableData?.totalRowCount ?? 0}
                data={tableData?.data ?? []}
                loading={isLoading}
                emptyState={error || tableData?.data?.length === 0 ? emptyStateMessage : undefined}
                manualSorting
            />
        </TableProvider>
    );
};
